'use memo'

import { useHistory, useLocation } from 'react-router-dom'
import type { TeamFinderFilters } from '@sportninja/common/features/teamFinder/types'
import { filtersToParams } from '@sportninja/common/features/teamFinder/filterParams'

interface UseFilterUrlSyncResult {
  /** Write the searched filters to the query string. */
  writeFilters: (filters: TeamFinderFilters) => void
  /** Drop every filter param, leaving the bare path. */
  clearFilters: () => void
}

/**
 * Keeps the address bar describing the search on screen, so it can be copied
 * and shared.
 *
 * Writes on SEARCH, not on edit: the URL should describe results the user
 * actually asked for. Syncing every keystroke would produce links to filter
 * combinations nobody ever ran.
 *
 * Uses `push`, so each search is a history entry and Back returns to the
 * previous search rather than leaving the page.
 */
export const useFilterUrlSync = (): UseFilterUrlSyncResult => {
  'use memo'

  const history = useHistory()
  const { pathname } = useLocation()

  const writeFilters = (filters: TeamFinderFilters) => {
    const search = new URLSearchParams(filtersToParams(filters)).toString()
    history.push({ pathname, search })
  }

  // `replace`, not `push`: clearing is an undo of the current view, and a
  // history entry for "the empty form" is only something to click Back through.
  const clearFilters = () => history.replace({ pathname, search: '' })

  return { writeFilters, clearFilters }
}
